import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Slider } from 'primereact/slider';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api from '../../services/api';
import {
  BORDER_CITIES_BY_STATE,
  BORDER_OPTIONS,
  BORDER_STATES,
} from '../../constants/constants';
import ChatStoriesPanel from './ChatStoriesPanel';
import './ChatModeration.css';

function DirectionSwitch({ city, value, counts, onChange }) {
  const options = [
    { id: 'mx_to_us', label: directionLabel('mx_to_us', city), count: counts.toUsa },
    { id: 'us_to_mx', label: directionLabel('us_to_mx', city), count: counts.toCity },
  ];
  return (
    <div className="chat-mod-direction" role="tablist" aria-label="Dirección del chat">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          aria-selected={value === option.id}
          className={value === option.id ? 'active' : ''}
          onClick={() => onChange(option.id)}
        >
          <span>{option.label}</span>
          <span className="chat-mod-direction-count">{option.count}</span>
        </button>
      ))}
    </div>
  );
}

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
}

function formatClock(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatCrossingElapsed(seconds, minutes) {
  const total =
    Number.isFinite(Number(seconds)) && Number(seconds) > 0
      ? Math.floor(Number(seconds))
      : Math.max(0, Math.round(Number(minutes) || 0) * 60);
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return s > 0 ? `${h}h ${m}m ${s}s` : `${h}h ${m}m`;
  return s > 0 ? `${m} min ${s}s` : `${m} min`;
}

function formatLiveElapsed(startedAt, nowMs) {
  const start = new Date(startedAt).getTime();
  const total = Math.max(0, Math.floor((nowMs - start) / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (d > 0 || h > 0) parts.push(`${h}h`);
  if (d > 0 || h > 0 || m > 0) parts.push(`${m}min`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

/** Same badge as Flutter Icons.verified / CommunityPublish. */
function VerifiedMark({ size = 16 }) {
  return (
    <span className="chat-mod-verified" title="Verificado" aria-label="Verificado">
      <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
        <path
          fill="currentColor"
          d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z"
        />
      </svg>
    </span>
  );
}

function resolveStateCode(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  const upper = v.toUpperCase();
  const byCode = BORDER_STATES.find((s) => s.code.toUpperCase() === upper);
  if (byCode) return byCode.code;
  const byName = BORDER_STATES.find(
    (s) => s.name.toLowerCase() === v.toLowerCase()
  );
  return byName?.code || v;
}

function stateDisplayName(raw) {
  const code = resolveStateCode(raw);
  if (!code) return 'Sin estado';
  return (
    BORDER_STATES.find((s) => s.code === code)?.name ||
    String(raw || '').trim() ||
    'Sin estado'
  );
}

function cityDisplayName(stateRaw, cityRaw) {
  const stateCode = resolveStateCode(stateRaw);
  const city = String(cityRaw || '').trim();
  if (!city) return 'Ciudad';
  const cities = BORDER_CITIES_BY_STATE[stateCode] || [];
  const byCode = cities.find((c) => c.code === city);
  if (byCode) return byCode.name;
  const byName = cities.find(
    (c) => c.name.toLowerCase() === city.toLowerCase()
  );
  return byName?.name || city;
}

function borderDisplayName(raw) {
  const v = String(raw || '').trim();
  if (!v) return '';
  return (
    BORDER_OPTIONS.find((b) => b.code === v || b.code === v.toLowerCase())
      ?.name || v
  );
}

function channelBelongsToPage(channel, pageIdOrIds) {
  if (!pageIdOrIds || !channel) return false;
  const wanted = new Set(
    (Array.isArray(pageIdOrIds) ? pageIdOrIds : [pageIdOrIds])
      .map((x) => String(x || ''))
      .filter(Boolean)
  );
  if (!wanted.size) return false;
  if (channel.entryPageId && wanted.has(String(channel.entryPageId))) {
    return true;
  }
  const ids = Array.isArray(channel.entryPageIds) ? channel.entryPageIds : [];
  return ids.some((x) => wanted.has(String(x)));
}

function channelDirection(ch) {
  return ch?.direction === 'us_to_mx' ? 'us_to_mx' : 'mx_to_us';
}

function directionLabel(direction, cityName) {
  const city = String(cityName || '').trim() || 'ciudad';
  return direction === 'us_to_mx' ? `USA → ${city}` : `${city} → USA`;
}

function channelDedupeKey(ch) {
  const name = normName(ch?.crossingName || ch?.displayName || ch?.mobileName);
  return `${channelDirection(ch)}|${name}`;
}

function preferChannel(current, next) {
  const currentCount = current.messageCount24h || 0;
  const nextCount = next.messageCount24h || 0;
  if (nextCount !== currentCount) return nextCount > currentCount ? next : current;
  const currentAt = current.lastMessageAt ? new Date(current.lastMessageAt).getTime() : 0;
  const nextAt = next.lastMessageAt ? new Date(next.lastMessageAt).getTime() : 0;
  if (nextAt !== currentAt) return nextAt > currentAt ? next : current;
  if (Boolean(next.cityGeoId) !== Boolean(current.cityGeoId)) {
    return next.cityGeoId ? next : current;
  }
  return current;
}

function channelLocationKey(ch) {
  return [ch?.border || '', ch?.state || '', ch?.city || '']
    .map((s) => String(s).trim().toLowerCase())
    .join('|');
}

function normName(value) {
  return String(value || '').trim().toLowerCase();
}

/** Unique crossing names defined on a City Geo doc (crossings[] + maps[]). */
function crossingNamesFromCityGeo(geo) {
  const names = new Set();
  for (const crossing of geo?.crossings || []) {
    const name = normName(crossing?.crossingName);
    if (name) names.add(name);
  }
  for (const map of geo?.maps || []) {
    const name = normName(map?.crossingName);
    if (name) names.add(name);
  }
  return names;
}

function channelMatchesCityGeoNames(channel, geoNames) {
  if (!geoNames || !geoNames.size) return false;
  const candidates = [
    channel?.crossingName,
    channel?.mobileName,
    channel?.displayName,
    ...(Array.isArray(channel?.crossingNames) ? channel.crossingNames : []),
    channel?.labels?.es,
    channel?.labels?.en,
    channel?.labels?.fr,
  ];
  return candidates.some((value) => geoNames.has(normName(value)));
}

function transportLabel(mode) {
  if (mode === 'pedestrian') return 'Peatonal';
  if (mode === 'commercial') return 'Comercial';
  return 'Auto';
}

function laneLabel(types) {
  const first = Array.isArray(types) ? types[0] : types;
  const value = String(first || 'standard').toLowerCase();
  if (value === 'sentri') return 'SENTRI';
  if (value === 'ready' || value === 'ready_lane') return 'Ready Lane';
  if (value === 'fast') return 'FAST';
  return 'General';
}

function slotMatchesChannel(slot, channel) {
  if (!slot || !channel) return false;
  if (channel.city && normName(slot.city) !== normName(channel.city)) return false;
  if (channel.scope === 'city') {
    const names = (channel.crossingNames || []).map(normName).filter(Boolean);
    if (!names.length) return true;
    return names.includes(normName(slot.crossingName)) ||
      names.includes(normName(slot.mobileName)) ||
      names.includes(normName(slot.displayName));
  }
  const channelNames = [
    channel.crossingName,
    channel.mobileName,
    channel.displayName,
  ]
    .map(normName)
    .filter(Boolean);
  const slotNames = [slot.crossingName, slot.mobileName, slot.displayName]
    .map(normName)
    .filter(Boolean);
  return channelNames.some((name) => slotNames.includes(name));
}

function slotMatchesFilters(slot, filterTransport, filterLane) {
  if (filterTransport) {
    const mode = String(slot.transportMode || '').toLowerCase();
    const ok =
      filterTransport === 'vehicle'
        ? mode === 'vehicle' || mode === 'passenger'
        : mode === filterTransport;
    if (!ok) return false;
  }
  if (filterLane) {
    const lanes = (slot.laneTypes || []).map((x) => String(x).toLowerCase());
    const ok =
      filterLane === 'ready_lane'
        ? lanes.includes('ready_lane') || lanes.includes('ready')
        : lanes.includes(filterLane) ||
          (filterLane === 'standard' && lanes.length === 0);
    if (!ok) return false;
  }
  return true;
}

const ENTRY_PAGE_SIZE = 20;
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '👁️'];

export default function ChatModeration() {
  const toast = useRef(null);
  const [channels, setChannels] = useState([]);
  const [cityGeos, setCityGeos] = useState([]);
  const [onlyCityGeo, setOnlyCityGeo] = useState(true);
  const [loadingBrowse, setLoadingBrowse] = useState(true);
  const [pageQuery, setPageQuery] = useState('');
  const [crossingQuery, setCrossingQuery] = useState('');
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [chatDirection, setChatDirection] = useState('mx_to_us');
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [reactingId, setReactingId] = useState(null);
  const [asCommunityPersona, setAsCommunityPersona] = useState(false);
  const [personaName, setPersonaName] = useState('');
  const [generatingName, setGeneratingName] = useState(false);
  const [entryVisibleCount, setEntryVisibleCount] = useState(ENTRY_PAGE_SIZE);
  const [filterTransport, setFilterTransport] = useState('');
  const [filterLane, setFilterLane] = useState('');
  const [filterCrossing, setFilterCrossing] = useState('');
  const [reportCrossing, setReportCrossing] = useState('');
  const [reportMode, setReportMode] = useState(false);
  const [crossingMode, setCrossingMode] = useState(false);
  const [crossingMinutes, setCrossingMinutes] = useState('');
  const [crossingSeconds, setCrossingSeconds] = useState('');
  const [reportTransport, setReportTransport] = useState('vehicle');
  const [reportLane, setReportLane] = useState('standard');
  const [reportWait, setReportWait] = useState(30);
  const [liveTracks, setLiveTracks] = useState([]);
  const [allLiveTracks, setAllLiveTracks] = useState([]);
  const [liveSlots, setLiveSlots] = useState([]);
  const [liveListOpen, setLiveListOpen] = useState(false);
  const [loadingAllLive, setLoadingAllLive] = useState(false);
  const [simSlotKey, setSimSlotKey] = useState(null);
  const [simNickname, setSimNickname] = useState('');
  const [simElapsed, setSimElapsed] = useState(8);
  const [simExpected, setSimExpected] = useState(20);
  const [simProgress, setSimProgress] = useState(0.15);
  const [simulating, setSimulating] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const pollRef = useRef(null);
  const messagesEndRef = useRef(null);

  const loadBrowse = useCallback(async () => {
    try {
      setLoadingBrowse(true);
      const [channelsRes, geosRes] = await Promise.all([
        api.get('/chat/channels', {
          params: { onlyCityGeo: onlyCityGeo ? '1' : '0' },
        }),
        api.get('/city_geos').catch(() => ({ data: [] })),
      ]);
      setChannels(channelsRes.data.items || []);
      setCityGeos(
        Array.isArray(geosRes.data)
          ? geosRes.data
          : geosRes.data?.items || []
      );
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los canales',
      });
    } finally {
      setLoadingBrowse(false);
    }
  }, [onlyCityGeo]);

  const bumpChannelActivity = useCallback((slug, { preview, delta = 1 } = {}) => {
    if (!slug) return;
    setChannels((prev) =>
      prev.map((ch) => {
        if (ch.slug !== slug) return ch;
        return {
          ...ch,
          messageCount24h: Math.max(0, (ch.messageCount24h || 0) + delta),
          lastMessageAt: new Date().toISOString(),
          lastMessagePreview:
            preview != null ? preview : ch.lastMessagePreview,
        };
      })
    );
    setSelected((prev) => {
      if (!prev || prev.slug !== slug) return prev;
      return {
        ...prev,
        messageCount24h: Math.max(0, (prev.messageCount24h || 0) + delta),
        lastMessageAt: new Date().toISOString(),
        lastMessagePreview:
          preview != null ? preview : prev.lastMessagePreview,
      };
    });
  }, []);

  const loadAllLiveTracks = useCallback(async () => {
    try {
      setLoadingAllLive(true);
      const { data } = await api.get('/live_tracks');
      setAllLiveTracks(data.items || []);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los recorridos',
      });
    } finally {
      setLoadingAllLive(false);
    }
  }, []);

  const loadLiveSlots = useCallback(async () => {
    try {
      const { data } = await api.get('/live_tracks/slots');
      setLiveSlots(data.items || []);
    } catch {
      setLiveSlots([]);
    }
  }, []);

  const openLiveList = async () => {
    setLiveListOpen(true);
    await loadAllLiveTracks();
  };

  const stopLiveTrack = (row) => {
    confirmDialog({
      header: 'Parar recorrido',
      message: `¿Parar el recorrido de ${row.nickname || 'este usuario'}? Dejará de verse en vivo.`,
      acceptLabel: 'Parar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await api.post(`/live_tracks/${row.id}/stop`);
          if (selected) loadLiveTracks(selected);
          if (liveListOpen) loadAllLiveTracks();
        } catch {
          toast.current?.show({
            severity: 'error',
            summary: 'No se pudo parar',
          });
        }
      },
    });
  };

  const startSimulation = async () => {
    const option = simSlotOptions.find((opt) => opt.value === simSlotKey);
    const slot = option?.slot;
    if (!slot) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Elige una fila',
        detail: 'Este crossing no tiene un slot listo para simular.',
      });
      return;
    }
    setSimulating(true);
    try {
      await api.post('/live_tracks/simulate', {
        cityGeoId: slot.cityGeoId,
        slotKey: slot.slotKey,
        nickname: simNickname.trim() || undefined,
        elapsedMinutes: simElapsed,
        expectedWaitMinutes: simExpected,
        pathProgress: simProgress,
        city: slot.city,
        state: slot.state,
        border: slot.border,
      });
      setSimNickname('');
      if (selected) loadLiveTracks(selected);
      if (liveListOpen) loadAllLiveTracks();
      toast.current?.show({
        severity: 'success',
        summary: 'Recorrido sintético iniciado',
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'No se pudo simular',
        detail: error?.response?.data?.message || error.message,
      });
    } finally {
      setSimulating(false);
    }
  };

  const loadLiveTracks = useCallback(async (channel, crossing) => {
    if (!channel?.city && !channel?.crossingName) {
      setLiveTracks([]);
      return;
    }
    try {
      const { data } = await api.get('/live_tracks', {
        params: {
          active: 'true',
          city: channel.city || undefined,
          crossingName: crossing || undefined,
        },
      });
      setLiveTracks(data.items || []);
    } catch {
      setLiveTracks([]);
    }
  }, []);

  const loadMessages = useCallback(async (slug, { quiet } = {}) => {
    if (!slug) return;
    try {
      if (!quiet) setLoadingMessages(true);
      const params = { limit: 80, includeDeleted: 0 };
      if (filterCrossing) params.crossing = filterCrossing;
      if (filterTransport || filterLane) {
        params.reportsOnly = true;
        if (filterTransport) params.transportMode = filterTransport;
        if (filterLane) params.laneType = filterLane;
      }
      const { data } = await api.get(`/chat/channels/${slug}/messages`, {
        params,
      });
      setMessages(data.items || []);
      if (data.channel) {
        setSelected((prev) => {
          if (!prev || prev.slug !== data.channel.slug) return prev;
          const next = { ...prev, ...data.channel };
          if (
            prev.messageCount24h === next.messageCount24h &&
            prev.lastMessageAt === next.lastMessageAt &&
            prev.lastMessagePreview === next.lastMessagePreview &&
            prev.crossingName === next.crossingName &&
            prev.city === next.city
          ) {
            return prev;
          }
          return next;
        });
      }
    } catch {
      if (!quiet) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los mensajes',
        });
      }
    } finally {
      if (!quiet) setLoadingMessages(false);
    }
  }, [filterTransport, filterLane, filterCrossing]);

  useEffect(() => {
    loadBrowse();
    loadLiveSlots();
  }, [loadBrowse, loadLiveSlots]);

  useEffect(() => {
    setEntryVisibleCount(ENTRY_PAGE_SIZE);
  }, [pageQuery]);

  useEffect(() => {
    const slug = selected?.slug;
    if (!slug) return undefined;
    loadMessages(slug);
    loadLiveTracks(selected, filterCrossing);
    pollRef.current = setInterval(() => {
      loadMessages(slug, { quiet: true });
      loadLiveTracks(selected, filterCrossing);
    }, 10000);
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      clearInterval(tick);
    };
  }, [selected?.slug, filterCrossing, loadMessages, loadLiveTracks]);

  useEffect(() => {
    if (!liveListOpen) return undefined;
    const poll = setInterval(() => {
      loadAllLiveTracks();
    }, 4000);
    return () => clearInterval(poll);
  }, [liveListOpen, loadAllLiveTracks]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const cityGeoByLocation = useMemo(() => {
    const map = new Map();
    for (const geo of cityGeos) {
      if (geo?.active === false) continue;
      const key = channelLocationKey(geo);
      if (!key || key === '||') continue;
      const names = crossingNamesFromCityGeo(geo);
      const prev = map.get(key) || {
        geoIds: new Set(),
        crossingNames: new Set(),
      };
      if (geo._id) prev.geoIds.add(String(geo._id));
      for (const name of names) prev.crossingNames.add(name);
      map.set(key, prev);
    }
    return map;
  }, [cityGeos]);

  const entryPageRows = useMemo(() => {
    // One row per city (border/state/city). ES+EN share the same family.
    const byLoc = new Map();
    for (const ch of channels) {
      if (onlyCityGeo) {
        const loc = channelLocationKey(ch);
        const geoInfo = cityGeoByLocation.get(loc);
        if (!geoInfo) continue;
        if (ch.scope === 'city') continue;
        if (
          !ch.cityGeoId &&
          !channelMatchesCityGeoNames(ch, geoInfo.crossingNames)
        ) {
          continue;
        }
      }
      const loc = channelLocationKey(ch);
      const pageIds = new Set();
      if (ch.entryPageId) pageIds.add(String(ch.entryPageId));
      for (const id of ch.entryPageIds || []) {
        if (id) pageIds.add(String(id));
      }
      if (!pageIds.size && (!loc || loc === '||')) continue;

      const key = loc && loc !== '||' ? `loc:${loc}` : `id:${[...pageIds][0]}`;
      const prev = byLoc.get(key) || {
        id: null,
        pageIds: new Set(),
        channelCount: 0,
        crossingCount: 0,
        messageCount24h: 0,
        lastMessageAt: null,
        city: ch.city,
        state: ch.state,
        border: ch.border,
        channelSlugs: new Set(),
      };

      for (const id of pageIds) prev.pageIds.add(id);
      // Prefer canonical entryPageId (ES after sync) as the row id.
      if (ch.entryPageId) {
        const eid = String(ch.entryPageId);
        if (!prev.id || prev.pageIds.has(eid)) prev.id = eid;
      } else if (!prev.id && pageIds.size) {
        prev.id = [...pageIds][0];
      }

      const slug = ch.slug || ch.id;
      if (slug && !prev.channelSlugs.has(slug)) {
        prev.channelSlugs.add(slug);
        prev.channelCount += 1;
        prev.messageCount24h += ch.messageCount24h || 0;
      }
      if (ch.scope !== 'city') {
        prev.crossingCount += 1;
      } else if (Array.isArray(ch.crossingNames)) {
        prev.crossingCount = Math.max(prev.crossingCount, ch.crossingNames.length);
      }
      if (
        ch.lastMessageAt &&
        (!prev.lastMessageAt ||
          new Date(ch.lastMessageAt) > new Date(prev.lastMessageAt))
      ) {
        prev.lastMessageAt = ch.lastMessageAt;
      }
      if (!prev.city && ch.city) prev.city = ch.city;
      if (!prev.state && ch.state) prev.state = ch.state;
      if (!prev.border && ch.border) prev.border = ch.border;
      byLoc.set(key, prev);
    }

    let rows = [...byLoc.values()].map((row) => {
      const stateRaw = row.state || '';
      const stateCode = resolveStateCode(stateRaw);
      const stateName = stateDisplayName(stateRaw);
      const cityName = cityDisplayName(stateRaw, row.city);
      const pageIds = [...row.pageIds];
      return {
        id: row.id || pageIds[0],
        pageIds,
        city: row.city,
        state: row.state,
        border: row.border,
        channelCount: row.channelCount,
        crossingCount: row.crossingCount,
        messageCount24h: row.messageCount24h,
        lastMessageAt: row.lastMessageAt,
        stateCode: stateCode || '_none',
        stateName,
        label: cityName,
        meta: [borderDisplayName(row.border)].filter(Boolean).join(' · '),
      };
    });

    const q = pageQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) => {
        const hay = `${row.label} ${row.meta} ${row.city || ''} ${
          row.state || ''
        } ${row.stateName} ${row.stateCode}`.toLowerCase();
        return hay.includes(q);
      });
    }

    rows.sort((a, b) => {
      const stateCmp = String(a.stateName).localeCompare(String(b.stateName));
      if (stateCmp !== 0) return stateCmp;
      const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(a.label).localeCompare(String(b.label));
    });
    return rows;
  }, [channels, pageQuery, onlyCityGeo, cityGeoByLocation]);

  const visibleEntryRows = useMemo(
    () => entryPageRows.slice(0, entryVisibleCount),
    [entryPageRows, entryVisibleCount]
  );

  const statePageTotals = useMemo(() => {
    const map = new Map();
    for (const row of entryPageRows) {
      const key = row.stateCode || '_none';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [entryPageRows]);

  const entryPageGroups = useMemo(() => {
    const map = new Map();
    for (const row of visibleEntryRows) {
      const key = row.stateCode || '_none';
      if (!map.has(key)) {
        map.set(key, {
          stateCode: key,
          stateName: row.stateName || 'Sin estado',
          pages: [],
        });
      }
      map.get(key).pages.push(row);
    }
    return [...map.values()].sort((a, b) =>
      String(a.stateName).localeCompare(String(b.stateName))
    );
  }, [visibleEntryRows]);

  const hasMoreEntryPages = entryVisibleCount < entryPageRows.length;

  const visibleLiveTracks = useMemo(() => {
    return liveTracks.filter((row) => {
      if (filterTransport) {
        const mode = String(row.transportMode || '').toLowerCase();
        const ok =
          filterTransport === 'vehicle'
            ? mode === 'vehicle' || mode === 'passenger'
            : mode === filterTransport;
        if (!ok) return false;
      }
      if (filterLane) {
        const lanes = [row.laneType, ...(row.laneTypes || [])].map((x) =>
          String(x || '').toLowerCase()
        );
        const ok =
          filterLane === 'ready_lane'
            ? lanes.includes('ready_lane') || lanes.includes('ready')
            : lanes.includes(filterLane);
        if (!ok) return false;
      }
      return true;
    });
  }, [liveTracks, filterTransport, filterLane]);

  const channelSlots = useMemo(
    () => liveSlots.filter((slot) => slotMatchesChannel(slot, selected)),
    [liveSlots, selected]
  );

  const simSlotOptions = useMemo(() => {
    const filtered = channelSlots.filter((slot) =>
      slotMatchesFilters(slot, filterTransport, filterLane)
    );
    const list = filtered.length ? filtered : channelSlots;
    return list.map((slot) => ({
      label: `${slot.mobileName || slot.displayName || slot.crossingName} · ${transportLabel(
        slot.transportMode
      )} · ${laneLabel(slot.laneTypes)}`,
      value: `${slot.cityGeoId}::${slot.slotKey}`,
      slot,
    }));
  }, [channelSlots, filterTransport, filterLane]);

  useEffect(() => {
    if (!simSlotOptions.length) {
      setSimSlotKey(null);
      return;
    }
    if (!simSlotOptions.some((opt) => opt.value === simSlotKey)) {
      setSimSlotKey(simSlotOptions[0].value);
    }
  }, [simSlotOptions, simSlotKey]);

  const selectedPageRow = entryPageRows.find((r) => r.id === selectedPageId);

  /** Same key the mobile app uses for stories (`stateCode|cityCode`). */
  const storiesCityKey = selectedPageRow
    ? `${String(selectedPageRow.state || '').trim()}|${String(selectedPageRow.city || '').trim()}`
    : '';

  const pageCrossingChannels = useMemo(() => {
    if (!selectedPageRow) return [];
    const pageIds = selectedPageRow.pageIds?.length
      ? selectedPageRow.pageIds
      : [selectedPageRow.id];
    const loc = channelLocationKey(selectedPageRow);
    const geoInfo = cityGeoByLocation.get(loc);
    let list = channels.filter((ch) => {
      if (ch.scope === 'city') return false;
      const belongs =
        channelBelongsToPage(ch, pageIds) ||
        channelLocationKey(ch) === loc;
      if (!belongs) return false;
      if (!onlyCityGeo) return true;
      if (ch.cityGeoId) return true;
      return channelMatchesCityGeoNames(ch, geoInfo?.crossingNames);
    });
    // One room per crossing and direction, same as the app.
    const byCrossing = new Map();
    for (const ch of list) {
      const key = channelDedupeKey(ch);
      if (!key.endsWith('|') && byCrossing.has(key)) {
        byCrossing.set(key, preferChannel(byCrossing.get(key), ch));
      } else if (key && !key.endsWith('|')) {
        byCrossing.set(key, ch);
      }
    }
    list = [...byCrossing.values()];
    list.sort((a, b) => {
      const nameCmp = String(a.displayName || a.crossingName || '').localeCompare(
        String(b.displayName || b.crossingName || ''),
        'es'
      );
      if (nameCmp !== 0) return nameCmp;
      const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return tb - ta;
    });
    return list;
  }, [channels, selectedPageRow, onlyCityGeo, cityGeoByLocation]);

  const directionCounts = useMemo(() => {
    let toUsa = 0;
    let toCity = 0;
    for (const ch of pageCrossingChannels) {
      if (channelDirection(ch) === 'us_to_mx') toCity += 1;
      else toUsa += 1;
    }
    return { toUsa, toCity };
  }, [pageCrossingChannels]);

  const crossingsForPage = useMemo(() => {
    const directed = pageCrossingChannels.filter(
      (ch) => channelDirection(ch) === chatDirection
    );
    const q = crossingQuery.trim().toLowerCase();
    if (!q) return directed;
    return directed.filter((ch) => {
      const hay = `${ch.displayName || ''} ${ch.crossingName || ''} ${
        ch.lastMessagePreview || ''
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [pageCrossingChannels, crossingQuery, chatDirection]);

  // Keep selection if it still belongs to the page; otherwise clear so the
  // crossing list is shown.
  useEffect(() => {
    if (!selectedPageRow) return;
    if (!selected) return;
    const stillThere = pageCrossingChannels.some(
      (ch) => (ch.slug || ch.id) === (selected.slug || selected.id)
    );
    if (stillThere) return;
    setSelected(null);
    setMessages([]);
    setDraft('');
    setFilterCrossing('');
  }, [selectedPageRow?.id, pageCrossingChannels, selected?.slug]);

  const crossingOptions = useMemo(() => {
    const names = new Set();
    if (selected?.crossingName) names.add(selected.crossingName);
    for (const name of selected?.crossingNames || []) {
      if (name) names.add(name);
    }
    for (const slot of liveSlots) {
      if (!slotMatchesChannel(slot, selected)) continue;
      if (slot.crossingName) names.add(slot.crossingName);
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'es'));
  }, [selected, liveSlots]);

  useEffect(() => {
    const fallback =
      selected?.crossingName ||
      selected?.displayName ||
      crossingOptions[0] ||
      '';
    if (!reportCrossing && fallback) {
      setReportCrossing(fallback);
    }
  }, [crossingOptions, reportCrossing, selected?.crossingName, selected?.displayName]);

  const selectPage = (pageId) => {
    setSelectedPageId(pageId);
    setCrossingQuery('');
    setFilterCrossing('');
    setSelected(null);
    setMessages([]);
    setDraft('');
  };

  const openChannel = (ch) => {
    setChatDirection(channelDirection(ch));
    setSelected(ch);
    setMessages([]);
    setDraft('');
  };

  const switchDirection = (next) => {
    if (next === chatDirection) return;
    setChatDirection(next);
    if (!selected) return;
    const currentName = normName(selected.crossingName || selected.displayName);
    const match = pageCrossingChannels.find((ch) => {
      if (channelDirection(ch) !== next) return false;
      if (
        selected.cityGeoId &&
        ch.cityGeoId &&
        String(ch.cityGeoId) === String(selected.cityGeoId)
      ) {
        return true;
      }
      const name = normName(ch.crossingName || ch.displayName);
      return currentName && name === currentName;
    });
    if (match) {
      openChannel(match);
      return;
    }
    setSelected(null);
    setMessages([]);
    setDraft('');
    setFilterCrossing('');
  };

  const generatePersonaName = useCallback(async () => {
    try {
      setGeneratingName(true);
      const { data } = await api.get('/chat/random-name', {
        params: { lang: 'es' },
      });
      if (data?.name) setPersonaName(data.name);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Nickname',
        detail: 'No se pudo generar un nombre',
      });
    } finally {
      setGeneratingName(false);
    }
  }, []);

  const crossingElapsedSeconds = () => {
    const minutes = Math.max(0, Math.floor(Number(crossingMinutes) || 0));
    const seconds = Math.max(0, Math.floor(Number(crossingSeconds) || 0));
    return minutes * 60 + seconds;
  };

  const sendOfficial = async () => {
    const text = draft.trim();
    if (!selected?.slug || sending) return;
    if (!reportMode && !crossingMode && !text) return;
    const elapsedSeconds = crossingMode ? crossingElapsedSeconds() : 0;
    if (crossingMode && elapsedSeconds < 1) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Cruce',
        detail: 'Indica los minutos o segundos en la fila',
      });
      return;
    }
    const asPersona = asCommunityPersona || crossingMode;
    const crossingName =
      reportCrossing ||
      filterCrossing ||
      selected.crossingName ||
      selected.crossingNames?.[0];
    const endedAt = new Date();
    const startedAt = new Date(endedAt.getTime() - elapsedSeconds * 1000);
    setSending(true);
    try {
      const body = crossingMode
        ? {
            type: 'crossing',
            clientId: `admin-crossing-${Date.now()}`,
            asCommunityPersona: true,
            personaName: personaName.trim() || undefined,
            report: {
              port: selected.portNumber || selected.crossingName || 'admin',
              laneType: reportLane,
              transportMode: reportTransport,
              crossingName,
              elapsedSeconds,
              elapsedMinutes: Math.floor(elapsedSeconds / 60),
              startedAt: startedAt.toISOString(),
              endedAt: endedAt.toISOString(),
              status: 'approved',
              source: 'live',
              endReason: 'manual',
            },
            crossingName,
          }
        : reportMode
        ? {
            type: 'report',
            text,
            clientId: `admin-report-${Date.now()}`,
            asCommunityPersona,
            personaName: asCommunityPersona
              ? personaName.trim() || undefined
              : undefined,
            report: {
              port: selected.portNumber || selected.crossingName || 'admin',
              laneType: reportLane,
              transportMode: reportTransport,
              crossingName,
              text,
              waitBucketMinutes: Number(reportWait) || null,
              status: 'approved',
            },
            crossingName,
          }
        : {
            type: 'text',
            text,
            clientId: `admin-${Date.now()}`,
            asCommunityPersona,
            personaName: asCommunityPersona
              ? personaName.trim() || undefined
              : undefined,
            crossingName: filterCrossing || undefined,
          };
      const { data } = await api.post(
        `/chat/channels/${selected.slug}/messages`,
        body
      );
      setDraft('');
      const item = data?.item;
      if (item) {
        setMessages((prev) => [...prev, item]);
      } else {
        await loadMessages(selected.slug, { quiet: true });
      }
      bumpChannelActivity(selected.slug, {
        preview: crossingMode
          ? `Cruce · ${formatCrossingElapsed(elapsedSeconds)}`
          : reportMode
            ? `📋 ${(text || 'Reporte').slice(0, 100)}`
            : text.slice(0, 120),
        delta: 1,
      });
      if (asCommunityPersona && !crossingMode) {
        generatePersonaName();
      }
      const sentName = data?.personaName || personaName.trim();
      toast.current?.show({
        severity: 'success',
        summary: 'Enviado',
        detail: crossingMode
          ? `Cruce publicado${sentName ? ` como ${sentName}` : ''}`
          : reportMode
            ? 'Reporte publicado en el chat'
            : asPersona
              ? sentName
                ? `Enviado como ${sentName}`
                : 'Enviado con nombre sintético'
              : 'Mensaje oficial de BorderGarita',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.message || 'No se pudo enviar',
      });
    } finally {
      setSending(false);
    }
  };

  const toggleReaction = async (msg, emoji) => {
    if (!msg?.id || reactingId) return;
    setReactingId(msg.id);
    try {
      const { data } = await api.put(`/chat/messages/${msg.id}/reactions`, {
        emoji,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id
            ? {
                ...m,
                reactionCounts: data?.reactionCounts || {},
                myEmojis: data?.myEmojis || [],
              }
            : m
        )
      );
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Reacción',
        detail: err?.response?.data?.message || 'No se pudo reaccionar',
      });
    } finally {
      setReactingId(null);
    }
  };

  const deleteMessage = (msg) => {
    confirmDialog({
      header: 'Eliminar mensaje',
      message: '¿Eliminar este mensaje del chat? Los usuarios dejarán de verlo.',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        try {
          await api.delete(`/chat/messages/${msg.id}`);
          setMessages((prev) => prev.filter((m) => m.id !== msg.id));
          bumpChannelActivity(selected?.slug, { delta: -1 });
          toast.current?.show({
            severity: 'success',
            summary: 'Eliminado',
          });
        } catch {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo eliminar',
          });
        }
      },
    });
  };

  const renderSimForm = () => (
    <div className="chat-mod-sim">
      {channelSlots.length === 0 ? (
        <p className="chat-mod-empty" style={{ margin: 0 }}>
          Este crossing no tiene fila lista (geocerca + path). Configúralo en
          City Geo.
        </p>
      ) : (
        <>
          {simSlotOptions.length > 1 ? (
            <label className="chat-mod-sim-field chat-mod-sim-wide">
              <span>Fila</span>
              <Dropdown
                value={simSlotKey}
                options={simSlotOptions}
                onChange={(e) => setSimSlotKey(e.value)}
                placeholder="Crossing · transporte · carril"
              />
            </label>
          ) : (
            <p className="chat-mod-sim-slot">{simSlotOptions[0]?.label}</p>
          )}
          <label className="chat-mod-sim-field">
            <span>Apodo</span>
            <InputText
              value={simNickname}
              onChange={(e) => setSimNickname(e.target.value)}
              placeholder="Aleatorio si vacío"
            />
          </label>
          <label className="chat-mod-sim-field">
            <span>Ya lleva (min)</span>
            <InputNumber
              value={simElapsed}
              onValueChange={(e) => setSimElapsed(e.value ?? 0)}
              min={0}
              max={240}
            />
          </label>
          <label className="chat-mod-sim-field">
            <span>Espera estimada</span>
            <InputNumber
              value={simExpected}
              onValueChange={(e) => setSimExpected(e.value ?? 0)}
              min={0}
              max={300}
            />
          </label>
          <label className="chat-mod-sim-field chat-mod-sim-wide">
            <span>Avance en el path ({Math.round(simProgress * 100)}%)</span>
            <Slider
              value={simProgress}
              onChange={(e) => setSimProgress(Number(e.value) || 0)}
              min={0}
              max={0.95}
              step={0.05}
            />
          </label>
          <div className="chat-mod-sim-actions">
            <Button
              type="button"
              label="Iniciar simulación"
              icon="pi pi-play"
              size="small"
              loading={simulating}
              disabled={!simSlotKey}
              onClick={startSimulation}
            />
          </div>
        </>
      )}
    </div>
  );

  const syncChannels = async () => {
    try {
      await api.post('/chat/channels/sync');
      await loadBrowse();
      toast.current?.show({
        severity: 'success',
        summary: 'Sincronizado',
        detail: 'Canales actualizados desde City Geos',
      });
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Sync falló',
      });
    }
  };

  return (
    <div className="chat-mod">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="chat-mod-header">
        <div>
          <p className="chat-mod-kicker">Community & Mobile</p>
          <h1>Chat por garita</h1>
          <p className="chat-mod-sub">
            Un chat por crossing name de City Geo. ES/EN comparten el mismo
            canal. Poll cada 10s.
          </p>
        </div>
        <div className="chat-mod-actions">
          <label className="chat-mod-citygeo-filter">
            <Checkbox
              inputId="only-city-geo"
              checked={onlyCityGeo}
              onChange={(e) => setOnlyCityGeo(Boolean(e.checked))}
            />
            <span>Solo City Geo</span>
          </label>
          <Button
            label="Sync channels"
            icon="pi pi-refresh"
            outlined
            onClick={syncChannels}
          />
          <Button
            label="Reload"
            icon="pi pi-replay"
            text
            onClick={loadBrowse}
            loading={loadingBrowse}
          />
        </div>
      </div>

      <div className="chat-mod-layout chat-mod-layout-2">
        <Card className="chat-mod-col" title="1. Entry pages">
          <IconField iconPosition="left" className="chat-mod-search">
            <InputIcon className="pi pi-search" />
            <InputText
              value={pageQuery}
              onChange={(e) => setPageQuery(e.target.value)}
              placeholder="Buscar estado / ciudad…"
              className="w-full"
            />
          </IconField>
          {loadingBrowse ? (
            <p>Cargando…</p>
          ) : entryPageGroups.length === 0 ? (
            <p className="chat-mod-empty">
              Sin entry pages con canales. Usa Sync channels.
            </p>
          ) : (
            <>
              <ul className="chat-mod-channel-list">
                {entryPageGroups.map((group) => (
                  <li key={group.stateCode} className="chat-mod-state-group">
                    <div className="chat-mod-state-header">
                      <span className="chat-mod-state-name">
                        {group.stateName}
                      </span>
                      <span className="chat-mod-state-count">
                        {statePageTotals.get(group.stateCode) ||
                          group.pages.length}
                      </span>
                    </div>
                    <ul className="chat-mod-state-pages">
                      {group.pages.map((row) => (
                        <li key={row.id}>
                          <button
                            type="button"
                            className={
                              selectedPageId === row.id
                                ? 'chat-mod-channel chat-mod-channel-nested active'
                                : 'chat-mod-channel chat-mod-channel-nested'
                            }
                            onClick={() => selectPage(row.id)}
                          >
                            <span className="chat-mod-channel-name">
                              {row.label}
                            </span>
                            <span className="chat-mod-channel-meta">
                              {row.meta || '—'} ·{' '}
                              {row.crossingCount || row.channelCount} garitas
                            </span>
                            <span className="chat-mod-channel-meta">
                              {row.messageCount24h || 0} msgs/24h
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
              <div className="chat-mod-pager">
                <span className="chat-mod-pager-meta">
                  {Math.min(entryVisibleCount, entryPageRows.length)} /{' '}
                  {entryPageRows.length}
                </span>
                {hasMoreEntryPages ? (
                  <Button
                    label="Cargar más"
                    icon="pi pi-angle-down"
                    text
                    size="small"
                    onClick={() =>
                      setEntryVisibleCount((n) => n + ENTRY_PAGE_SIZE)
                    }
                  />
                ) : null}
              </div>
            </>
          )}
        </Card>

        <Card
          className="chat-mod-thread"
          title={
            selected
              ? `2. Chat · ${selected.displayName || selected.crossingName} · ${directionLabel(channelDirection(selected), selectedPageRow?.label)}`
              : selectedPageRow
                ? `2. Garitas · ${selectedPageRow.label}`
                : '2. Selecciona una ciudad'
          }
        >
          {!selectedPageRow ? (
            <p className="chat-mod-empty">
              Elige una ciudad para ver sus chats por garita.
            </p>
          ) : !selected ? (
            <>
              <DirectionSwitch
                city={selectedPageRow.label}
                value={chatDirection}
                counts={directionCounts}
                onChange={switchDirection}
              />
              <ChatStoriesPanel
                cityKey={storiesCityKey}
                cityLabel={selectedPageRow.label}
                toast={toast}
                embedded
              />
              <IconField iconPosition="left" className="chat-mod-search">
                <InputIcon className="pi pi-search" />
                <InputText
                  value={crossingQuery}
                  onChange={(e) => setCrossingQuery(e.target.value)}
                  placeholder="Buscar garita…"
                  className="w-full"
                />
              </IconField>
              {crossingsForPage.length === 0 ? (
                <p className="chat-mod-empty">
                  Sin chats {directionLabel(chatDirection, selectedPageRow.label)}.
                  Usa Sync channels si falta esta dirección.
                </p>
              ) : (
                <ul className="chat-mod-channel-list">
                  {crossingsForPage.map((ch) => (
                    <li key={ch.slug || ch.id}>
                      <button
                        type="button"
                        className="chat-mod-channel"
                        onClick={() => openChannel(ch)}
                      >
                        <span className="chat-mod-channel-name">
                          {ch.displayName || ch.crossingName}
                        </span>
                        <span className="chat-mod-channel-meta">
                          {ch.lastMessagePreview || 'Sin mensajes'}
                        </span>
                        <span className="chat-mod-channel-meta">
                          {ch.messageCount24h || 0} msgs/24h
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <>
              <div className="chat-mod-toolbar mb-2">
                <Button
                  type="button"
                  label="Garitas"
                  icon="pi pi-arrow-left"
                  text
                  size="small"
                  onClick={() => {
                    setSelected(null);
                    setMessages([]);
                    setDraft('');
                    setFilterCrossing('');
                  }}
                />
              </div>
              <DirectionSwitch
                city={selectedPageRow.label}
                value={chatDirection}
                counts={directionCounts}
                onChange={switchDirection}
              />
              <ChatStoriesPanel
                cityKey={storiesCityKey}
                cityLabel={selectedPageRow.label}
                toast={toast}
                embedded
              />
              <div className="chat-mod-live">
                <div className="chat-mod-live-head">
                  <strong>
                    {visibleLiveTracks.length === 1
                      ? '1 recorrido en vivo'
                      : `${visibleLiveTracks.length} recorridos en vivo`}
                  </strong>
                  <button
                    type="button"
                    className="chat-mod-live-link"
                    onClick={openLiveList}
                  >
                    Ver lista completa
                  </button>
                </div>
                {renderSimForm()}
                {visibleLiveTracks.length === 0 ? (
                  <p className="chat-mod-empty" style={{ margin: 0 }}>
                    Nadie está compartiendo en esta ciudad.
                  </p>
                ) : (
                  <ul className="chat-mod-live-list">
                    {visibleLiveTracks.map((row) => (
                      <li key={row.id}>
                        <span>
                          {row.nickname}
                          {row.synthetic ? ' · sintético' : ''}
                          {' · '}
                          {row.laneLabel || row.transportMode}
                        </span>
                        <b>{formatLiveElapsed(row.startedAt, nowMs)}</b>
                        <Button
                          type="button"
                          label="Parar"
                          size="small"
                          text
                          severity="danger"
                          onClick={() => stopLiveTrack(row)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="chat-mod-filters flex gap-2 flex-wrap mb-3 align-items-end">
                {selected?.scope === 'city' ? (
                  <div>
                    <label className="text-sm font-semibold">Crossing</label>
                    <select
                      className="p-inputtext p-component w-full mt-1"
                      value={filterCrossing}
                      onChange={(e) => setFilterCrossing(e.target.value)}
                    >
                      <option value="">Todas</option>
                      {crossingOptions.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div>
                  <label className="text-sm font-semibold">Filtro transport</label>
                  <select
                    className="p-inputtext p-component w-full mt-1"
                    value={filterTransport}
                    onChange={(e) => setFilterTransport(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="vehicle">Auto</option>
                    <option value="pedestrian">Peatonal</option>
                    <option value="commercial">Comercial</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold">Filtro lane</label>
                  <select
                    className="p-inputtext p-component w-full mt-1"
                    value={filterLane}
                    onChange={(e) => setFilterLane(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="standard">General</option>
                    <option value="sentri">SENTRI</option>
                    <option value="ready_lane">Ready Lane</option>
                    <option value="fast">FAST</option>
                  </select>
                </div>
                <Button
                  type="button"
                  label="Limpiar"
                  outlined
                  size="small"
                  onClick={() => {
                    setFilterTransport('');
                    setFilterLane('');
                    setFilterCrossing('');
                  }}
                />
              </div>
              <div className="chat-mod-messages">
                {loadingMessages && messages.length === 0 ? (
                  <p>Cargando mensajes…</p>
                ) : null}
                {!loadingMessages && messages.length === 0 ? (
                  <p className="chat-mod-empty">Aún no hay mensajes.</p>
                ) : null}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={
                      msg.isOfficial
                        ? 'chat-mod-bubble official'
                        : 'chat-mod-bubble'
                    }
                  >
                    <div className="chat-mod-bubble-head">
                      <strong>{msg.displayName}</strong>
                      {msg.isOfficial ? <VerifiedMark size={16} /> : null}
                      {msg.crossingName || msg.report?.crossingName ? (
                        <span className="chat-mod-time">
                          {msg.crossingName || msg.report?.crossingName}
                        </span>
                      ) : null}
                      {msg.type === 'crossing' || msg.report?.source === 'live' ? (
                        <span className="chat-mod-time">cruce</span>
                      ) : msg.type === 'report' ? (
                        <span className="chat-mod-time">reporte</span>
                      ) : null}
                      <span className="chat-mod-time">
                        {formatTime(msg.createdAt)}
                      </span>
                      <Button
                        icon="pi pi-trash"
                        rounded
                        text
                        severity="danger"
                        size="small"
                        onClick={() => deleteMessage(msg)}
                        aria-label="Eliminar"
                      />
                    </div>
                    {msg.type === 'audio' ? (
                      <div className="chat-mod-audio">
                        <audio controls src={msg.audio?.url} preload="none" />
                        <span>
                          {Math.round((msg.audio?.durationMs || 0) / 1000)}s
                        </span>
                      </div>
                    ) : msg.type === 'report' || msg.type === 'crossing' ? (
                      <div>
                        <div className="flex gap-2 flex-wrap mb-2">
                          <span className="p-tag p-tag-rounded">
                            {msg.report?.transportMode || '—'}
                          </span>
                          <span className="p-tag p-tag-rounded p-tag-info">
                            {msg.report?.laneLabel || msg.report?.laneType || '—'}
                          </span>
                          {msg.type === 'crossing' ||
                          msg.report?.source === 'live' ? (
                            <span className="p-tag p-tag-rounded p-tag-success">
                              {formatCrossingElapsed(
                                msg.report?.elapsedSeconds,
                                msg.report?.elapsedMinutes
                              )}
                            </span>
                          ) : msg.report?.waitBucketMinutes != null ? (
                            <span className="p-tag p-tag-rounded p-tag-success">
                              ~{msg.report.waitBucketMinutes} min
                            </span>
                          ) : null}
                        </div>
                        {msg.type === 'crossing' ||
                        msg.report?.source === 'live' ? (
                          <p className="chat-mod-crossing-times">
                            Inicio {formatClock(msg.report?.startedAt)} · Fin{' '}
                            {formatClock(msg.report?.endedAt)}
                          </p>
                        ) : null}
                        {msg.report?.photoUrl ? (
                          <img
                            src={msg.report.photoUrl}
                            alt=""
                            style={{
                              maxWidth: 220,
                              borderRadius: 8,
                              marginBottom: 8,
                            }}
                          />
                        ) : null}
                        {msg.type === 'crossing' ||
                        msg.report?.source === 'live' ? null : (
                          <p>{msg.report?.text || msg.text}</p>
                        )}
                      </div>
                    ) : msg.type === 'image' ? (
                      <img
                        src={msg.image?.url}
                        alt=""
                        style={{ maxWidth: 220, borderRadius: 8 }}
                      />
                    ) : msg.type === 'sticker' ? (
                      <img
                        src={msg.sticker?.url}
                        alt=""
                        style={{ width: 96, height: 96 }}
                      />
                    ) : (
                      <p>{msg.text}</p>
                    )}
                    <div className="chat-mod-reactions">
                      {REACTION_EMOJIS.map((emoji) => {
                        const count = Number(msg.reactionCounts?.[emoji]) || 0;
                        const mine = (msg.myEmojis || []).includes(emoji);
                        return (
                          <button
                            key={emoji}
                            type="button"
                            className={
                              mine
                                ? 'chat-mod-reaction mine'
                                : 'chat-mod-reaction'
                            }
                            disabled={reactingId === msg.id}
                            title={mine ? 'Quitar reacción' : 'Reaccionar'}
                            onClick={() => toggleReaction(msg, emoji)}
                          >
                            <span>{emoji}</span>
                            {count > 0 ? (
                              <span className="chat-mod-reaction-count">
                                {count}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <div className="chat-mod-composer">
                <label className="chat-mod-persona-check">
                  <Checkbox
                    inputId="chatAsPersona"
                    checked={asCommunityPersona}
                    onChange={(e) =>
                      setAsCommunityPersona(Boolean(e.checked))
                    }
                  />
                  <span>Nombre sintético</span>
                </label>
                <label className="chat-mod-persona-check">
                  <Checkbox
                    inputId="chatAsReport"
                    checked={reportMode}
                    onChange={(e) => {
                      const on = Boolean(e.checked);
                      setReportMode(on);
                      if (on) setCrossingMode(false);
                    }}
                  />
                  <span>Publicar como reporte (transport + lane)</span>
                </label>
                <label className="chat-mod-persona-check">
                  <Checkbox
                    inputId="chatAsCrossing"
                    checked={crossingMode}
                    onChange={(e) => {
                      const on = Boolean(e.checked);
                      setCrossingMode(on);
                      if (on) setReportMode(false);
                    }}
                  />
                  <span>Publicar cruce sintético (cronómetro)</span>
                </label>
                {reportMode || crossingMode ? (
                  <div className="flex gap-2 flex-wrap mb-2">
                    {crossingOptions.length ? (
                      <select
                        className="p-inputtext p-component"
                        value={reportCrossing}
                        onChange={(e) => setReportCrossing(e.target.value)}
                      >
                        {crossingOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    <select
                      className="p-inputtext p-component"
                      value={reportTransport}
                      onChange={(e) => setReportTransport(e.target.value)}
                    >
                      <option value="vehicle">Vehicular</option>
                      <option value="pedestrian">Peatonal</option>
                      <option value="commercial">Comercial</option>
                    </select>
                    <select
                      className="p-inputtext p-component"
                      value={reportLane}
                      onChange={(e) => setReportLane(e.target.value)}
                    >
                      <option value="standard">General</option>
                      <option value="sentri">Sentri</option>
                      <option value="ready_lane">Ready Lane</option>
                      <option value="fast">FAST</option>
                    </select>
                    {reportMode ? (
                      <InputText
                        type="number"
                        value={reportWait}
                        onChange={(e) => setReportWait(e.target.value)}
                        placeholder="Minutos"
                        style={{ width: 100 }}
                      />
                    ) : (
                      <>
                        <InputText
                          type="number"
                          min="0"
                          value={crossingMinutes}
                          onChange={(e) => setCrossingMinutes(e.target.value)}
                          placeholder="Minutos en fila"
                          style={{ width: 140 }}
                        />
                        <InputText
                          type="number"
                          min="0"
                          value={crossingSeconds}
                          onChange={(e) => setCrossingSeconds(e.target.value)}
                          placeholder="Segundos"
                          style={{ width: 110 }}
                        />
                      </>
                    )}
                  </div>
                ) : null}
                {crossingMode && crossingElapsedSeconds() > 0 ? (
                  <p className="chat-mod-empty" style={{ margin: '0 0 8px' }}>
                    {(() => {
                      const seconds = crossingElapsedSeconds();
                      const end = new Date();
                      const start = new Date(end.getTime() - seconds * 1000);
                      return `Inicio ${formatClock(start)} · Fin ${formatClock(end)} · ${formatCrossingElapsed(seconds)} en la fila`;
                    })()}
                  </p>
                ) : null}
                {asCommunityPersona || crossingMode ? (
                  <div className="chat-mod-persona-row">
                    <InputText
                      value={personaName}
                      onChange={(e) => setPersonaName(e.target.value)}
                      placeholder="Nickname"
                      className="w-full"
                    />
                    <Button
                      type="button"
                      icon="pi pi-refresh"
                      label="Generar"
                      outlined
                      loading={generatingName}
                      onClick={generatePersonaName}
                    />
                  </div>
                ) : null}
                {crossingMode ? null : (
                  <InputTextarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    autoResize
                    placeholder={
                      reportMode
                        ? 'Texto del reporte…'
                        : asCommunityPersona && personaName.trim()
                          ? `Mensaje como ${personaName.trim()}…`
                          : asCommunityPersona
                            ? 'Mensaje…'
                            : 'Mensaje oficial BorderGarita…'
                    }
                    className="w-full"
                  />
                )}
                <Button
                  label={
                    crossingMode
                      ? 'Publicar cruce sintético'
                      : reportMode
                        ? 'Publicar reporte en chat'
                        : asCommunityPersona && personaName.trim()
                          ? `Enviar como ${personaName.trim()}`
                          : asCommunityPersona
                            ? 'Enviar'
                            : 'Enviar como BorderGarita'
                  }
                  icon={
                    crossingMode
                      ? 'pi pi-stopwatch'
                      : reportMode
                        ? 'pi pi-flag'
                        : asCommunityPersona
                          ? 'pi pi-user'
                          : 'pi pi-verified'
                  }
                  onClick={sendOfficial}
                  loading={sending}
                  disabled={
                    crossingMode
                      ? crossingElapsedSeconds() < 1
                      : !reportMode && !draft.trim()
                  }
                />
              </div>
            </>
          )}
        </Card>
      </div>

      <Dialog
        header="Recorridos en vivo"
        visible={liveListOpen}
        onHide={() => setLiveListOpen(false)}
        style={{ width: 'min(720px, 94vw)' }}
        dismissableMask
      >
        {selected ? renderSimForm() : (
          <p className="chat-mod-empty">
            Elige un crossing para iniciar una simulación.
          </p>
        )}
        {loadingAllLive && allLiveTracks.length === 0 ? (
          <p>Cargando…</p>
        ) : allLiveTracks.length === 0 ? (
          <p className="chat-mod-empty">Nadie está compartiendo un recorrido.</p>
        ) : (
          <ul className="chat-mod-live-modal-list">
            {allLiveTracks.map((row) => (
              <li
                key={row.id}
                className={row.active ? '' : 'ended'}
              >
                <div>
                  <strong>{row.nickname}</strong>
                  <span>
                    {row.displayName || row.crossingName}
                    {row.synthetic ? ' · sintético' : ''}
                    {' · '}
                    {row.laneLabel || row.transportMode}
                  </span>
                </div>
                <b>
                  {formatLiveElapsed(row.startedAt, nowMs)}
                  {row.active ? '' : ' · fin'}
                </b>
                {row.active ? (
                  <Button
                    type="button"
                    label="Parar"
                    size="small"
                    severity="danger"
                    outlined
                    onClick={() => stopLiveTrack(row)}
                  />
                ) : (
                  <span className="chat-mod-time">{row.endReason || '—'}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </div>
  );
}
